import type { ReploMixedStyleValue } from "replo-runtime/store/utils/mixed-values";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import AssetLibraryPopover from "@v1/components/assets/AssetLibraryPopover";
import DynamicDataButton from "@v1/components/common/designSystem/DynamicDataButton";
import Input from "@v1/components/common/designSystem/Input";
import FormFieldXButton from "@v1/components/common/FormFieldXButton";
import { Badge } from "@v1/components/designSystem/badge/Badge";
import { useOverridableInput } from "@v1/hooks/useOverridableInput";
import { isDynamicDataValue } from "replo-runtime/shared/utils/dynamic-data";
import { isMixedStyleValue } from "replo-runtime/store/utils/mixed-values";

import { DynamicDataSelector } from "../../../DynamicDataSelector";

type InlineAssetSelectorProps = {
  asset?:
    | { type: "video"; src: string | undefined }
    | { type: "image"; src: string | undefined }
    | ReploMixedStyleValue;
  emptyTitle?: string;
  onClickSelectAsset?(): void;
  onRemoveAsset?(): void;
  allowRemoveAsset?: boolean;
  allowsDynamicData?: boolean;
  onClickDynamicData?(): void;
  alwaysShowRemoveButton?: boolean;
  onChange?(url: string, altText?: string | null): void;
  isDisabled?: boolean;
  inputName?: string;
  autocomplete?: "on" | "off";
  wrapperClassnames?: string;
  inputPlaceholder?: string;
  size: "sm" | "base";
  shouldOpenLibraryPopover?: boolean;
  isImageOnly?: boolean;
};

const InlineAssetSelector = ({
  asset,
  emptyTitle,
  onClickSelectAsset,
  onRemoveAsset,
  allowsDynamicData = false,
  allowRemoveAsset = true,
  alwaysShowRemoveButton,
  onChange,
  isDisabled,
  inputName,
  autocomplete = "off",
  wrapperClassnames,
  inputPlaceholder,
  size,
  shouldOpenLibraryPopover = true,
  isImageOnly = false,
}: InlineAssetSelectorProps) => {
  const [isAssetPickerPopoverOpen, setIsAssetPickerPopoverOpen] =
    React.useState(false);

  function _getTitle() {
    return !isMixedStyleValue(asset) ? asset?.src ?? emptyTitle : emptyTitle;
  }

  const valueIsDynamic = !isMixedStyleValue(asset)
    ? isDynamicDataValue(asset?.src)
    : false;
  const showInputField = !valueIsDynamic && Boolean(onChange);
  const srcInputProps = useOverridableInput({
    value: !isMixedStyleValue(asset) ? asset?.src ?? "" : "Mixed",
    onValueChange: onChange,
  });

  const showRemoveButton = (() => {
    if (!onRemoveAsset) {
      return false;
    }
    if (alwaysShowRemoveButton) {
      return true;
    }
    if (!allowRemoveAsset) {
      return false;
    }
    if (isMixedStyleValue(asset)) {
      return true;
    }
    return Boolean(asset?.src);
  })();

  return (
    <div className={twMerge("flex", wrapperClassnames)}>
      <div className="flex w-full items-center gap-1">
        <div className="flex w-full items-center gap-1 rounded-sm">
          {showInputField && (
            <Input
              {...srcInputProps}
              shouldSelectTextOnFocus
              placeholder={inputPlaceholder ?? "Source"}
              startEnhancer={
                <button
                  type="button"
                  data-testid="select-asset"
                  disabled={isDisabled}
                  onClick={() => {
                    if (shouldOpenLibraryPopover) {
                      setIsAssetPickerPopoverOpen(true);
                    } else {
                      onClickSelectAsset?.();
                    }
                  }}
                >
                  {isMixedStyleValue(asset) ? (
                    <Badge type="unknown" />
                  ) : (
                    <AssetLibraryPopover
                      isOpen={isAssetPickerPopoverOpen}
                      setIsOpen={setIsAssetPickerPopoverOpen}
                      onChangeSource={onChange}
                      assetTypes={[asset?.type ?? "image"]}
                      anchor={
                        // TODO (Gabe 2025-03-12, REPL-16485): This div is
                        // required because the Badge doesn't acccept forwarded
                        // props.
                        <div>
                          <Badge
                            type={asset?.type ?? "image"}
                            src={asset?.src ?? ""}
                            isFilled
                          />
                        </div>
                      }
                    />
                  )}
                </button>
              }
              layoutClassName="w-full"
              onOptionClick={onRemoveAsset}
              isDisabled={isDisabled}
              name={inputName}
              autoComplete={autocomplete}
              size={size}
              endEnhancer={() => {
                return (
                  showRemoveButton && (
                    <RemoveAssetButton onRemoveAsset={onRemoveAsset!} />
                  )
                );
              }}
            />
          )}
          {!showInputField && (
            <span
              className={twMerge(
                "cursor-pointer truncate text-xs",
                asset ? "text-default" : "text-muted",
              )}
              onClick={() => onClickSelectAsset?.()}
            >
              {_getTitle()}
            </span>
          )}
          {!showInputField && showRemoveButton && (
            <RemoveAssetButton onRemoveAsset={onRemoveAsset!} />
          )}
        </div>
        {allowsDynamicData && (
          <div className="flex">
            <DynamicDataSelector
              trigger={<DynamicDataButton />}
              targetType={isImageOnly ? "image" : "text/url"}
              side="left"
              sideOffset={222}
              onChange={(newValue) => {
                onChange?.(newValue);
              }}
              onRemove={onRemoveAsset}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const RemoveAssetButton: React.FC<{ onRemoveAsset(): void }> = ({
  onRemoveAsset,
}) => {
  return <FormFieldXButton onClick={() => onRemoveAsset()} />;
};

export default InlineAssetSelector;
